import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ScrollArea } from "@/components/ui/scroll-area";
const Terms = () => {
  return <div className="min-h-screen">
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
              Terms & Conditions
            </h1>
            <p className="text-muted-foreground mb-8">
              Last updated: November 2025
            </p>

            <ScrollArea className="h-[700px] rounded-lg border p-6">
              <div className="space-y-8 pr-4">
                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">1. Introduction</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    Welcome to Ace Anchor Pty Ltd (ABN 64 692 611 635). By accessing or using our website and services, 
                    you agree to comply with and be bound by these Terms and Conditions. Please read them carefully before 
                    booking any service.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">2. Services</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Ace Anchor provides concierge and settlement services to international students arriving in Australia. 
                    Our services include but are not limited to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-4">
                    <li>Airport pickup and transport coordination</li>
                    <li>Accommodation guidance and short-term assistance</li>
                    <li>Banking and phone setup support</li>
                    <li>University enrollment guidance</li>
                    <li>Cultural orientation and settlement help</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed font-semibold">
                    Note: Ace Anchor is not a migration or education consultancy and does not provide visa or legal advice.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">3. Booking and Payment</h2>
                  
                  <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-4">
                    <h3 className="font-semibold text-foreground mb-3">Important Payment Terms:</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="text-secondary font-bold">•</span>
                        <span><strong className="text-foreground">All prices are GST inclusive</strong> - There are no hidden charges or additional taxes</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-secondary font-bold">•</span>
                        <span><strong className="text-foreground">A non-refundable booking amount</strong> is required to secure your reservation</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-secondary font-bold">•</span>
                        <span><strong className="text-foreground">Balance is payable in Australia</strong> on arrival by card, cash, or payment link</span>
                      </li>
                    </ul>
                  </div>

                  <p className="text-muted-foreground leading-relaxed mb-3">
                    <strong>3.1.</strong> All bookings must be made at least 7 days prior to the student's arrival date.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    <strong>3.2.</strong> A non-refundable booking amount is required to secure your reservation. This covers 
                    administrative and coordination costs.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    <strong>3.3.</strong> The remaining balance must be paid in full at least 7 days prior to arrival to confirm 
                    your service.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    <strong>3.4.</strong> Payments can be made through our secure online payment gateway or via bank transfer. 
                    Upon arrival in Australia, the balance can be paid by card, cash, or via a secure payment link sent by our team.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    <strong>3.5.</strong> All prices displayed are inclusive of 10% GST (Goods and Services Tax) as required by 
                    Australian law. Prices are subject to change without prior notice, but confirmed bookings will be honored 
                    at the agreed rate.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">4. Cancellation & Refund Policy</h2>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    <strong>4.1.</strong> The booking amount is strictly non-refundable under all circumstances.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    <strong>4.2.</strong> Cancellations made more than 14 days before arrival will receive a 90% refund of the 
                    remaining balance (excluding the booking amount).
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    <strong>4.3.</strong> Cancellations made 7–14 days before arrival will receive a 50% refund of the remaining 
                    balance (excluding the booking amount).
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    <strong>4.4.</strong> Cancellations made less than 7 days before arrival are not eligible for any refund.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    <strong>4.5.</strong> Refunds, where applicable, will be processed within 10–15 business days after written 
                    confirmation of cancellation.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    <strong>4.6.</strong> To request a cancellation, email{" "}
                    <a href="mailto:support@aceanchorau.com" className="text-primary hover:underline">
                      support@aceanchorau.com
                    </a>{" "}
                    with your booking ID and registered details.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">5. Liability</h2>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    <strong>5.1.</strong> Ace Anchor will make every effort to provide services as described, but we are not liable 
                    for events beyond our control, including:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-4">
                    <li>Flight delays or cancellations</li>
                    <li>Visa issues or rejections</li>
                    <li>University or government policy changes</li>
                    <li>Personal property loss or damage</li>
                    <li>Third-party service failures</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed">
                    <strong>5.2.</strong> Our total liability shall not exceed the amount paid for our services.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">6. Client Responsibilities</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Clients must provide accurate and complete information including:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-4">
                    <li>Valid passport and visa documentation</li>
                    <li>Flight and arrival details</li>
                    <li>Emergency contact information</li>
                    <li>Any medical or special requirements</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed">
                    Failure to provide accurate information may result in delayed or incomplete service delivery.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">7. Privacy</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    We are committed to protecting your privacy and handling your data in line with Australian Privacy Principles 
                    (APPs). Please refer to our{" "}
                    <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>{" "}
                    for more details on how we collect, store, and use your personal information.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">8. Force Majeure</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    Ace Anchor shall not be held responsible for delays or failure in performance caused by events beyond our 
                    control, including natural disasters, pandemics, government restrictions, or airline disruptions.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">9. Changes to Terms</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    Ace Anchor reserves the right to update or modify these Terms at any time without prior notice. Updated Terms 
                    will be effective immediately upon posting on our website. Continued use of our services indicates acceptance 
                    of any modified Terms.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">10. Governing Law</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    These Terms and Conditions are governed by the laws of Victoria, Australia, and any disputes shall be resolved 
                    in the jurisdiction of its courts.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-4 text-foreground">11. Contact Information</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    For any questions regarding these Terms and Conditions, please contact us at:
                  </p>
                  <div className="space-y-2 text-muted-foreground">
                    <p className="font-semibold">Ace Anchor Pty Ltd</p>
                    <p>
                      📧 Email:{" "}
                      <a href="mailto:support@aceanchorau.com" className="text-primary hover:underline">
                        support@aceanchorau.com
                      </a>
                    </p>
                    <p>
                    📱 WhatsApp:{" "}
                      <a href="https://wa.me/61403601970" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">+61 0403 601 970</a>
                    </p>
                    <p>🏢 Address: Melbourne, Victoria, Australia</p>
                  </div>
                </section>
              </div>
            </ScrollArea>
          </div>
        </div>
      </main>
      <Footer />
    </div>;
};
export default Terms;